import React, { useState, useEffect, useId, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  CheckIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getTests, addTestToExam } from "../../services/Exams/ExamsService";
import { getEntityLabel, getEntitySearchText } from "../../utils/entityLabel";
import { getApiErrorCode, getCodedErrorMessage } from "../../utils/apiError";
import i18n from "../../lang/i18n";

// A Headless UI dialog (the focus stays in it; Escape and a click outside close it, not while tests are being
// added). `onTestAdded()`: tests were added, for the exam's page to close this and read the exam again;
// `onExamChanged(err)`: the exam turned out archived (409 exam_archived), for its page to close this, say why and read
// it again; `onClose()`: nothing was added.
const AddTestToExam = ({ examUuid, onClose, onTestAdded, onExamChanged, existingTests = [] }) => {
  const { t } = useTranslation();
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTests, setSelectedTests] = useState([]); // Changed to array
  const listId = useId();
  const closeRef = useRef(null);
  const searchRef = useRef(null);
  const errorRef = useRef(null);
  // Tests added by a click whose other tests failed: they are in the exam, which its page reads again on close.
  const addedRef = useRef(0);

  // The tests already in the exam (their uuids, as one key): the list is read again when they change.
  const existingKey = existingTests.map(examTest => examTest.test?.uuid).filter(Boolean).join(',');

  useEffect(() => {
    const existingTestUuids = new Set(existingKey ? existingKey.split(',') : []);
    const fetchTests = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching available tests...');
        // The server pages by 30 by default, and the search below only filters what was loaded.
        const fetchedData = await getTests({ params: { page: 1, page_size: 500, ordering: '-created' } });
        const data = fetchedData?.results;
        console.log('Available tests:', data);
        
        if (data && Array.isArray(data)) {
          // Filter tests that haven't been added to exam yet
          const availableTests = data.filter(test => !existingTestUuids.has(test.uuid));
          setTests(availableTests);
          setFilteredTests(availableTests);
        } else {
          setTests([]);
          setFilteredTests([]);
        }
      } catch (err) {
        console.error('Error fetching tests:', err);
        setError(i18n.t('exam_bank.select_tests.load_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [existingKey]);

  // Filter tests by search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTests(tests);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = tests.filter((test) => {
        const categoryText = getEntitySearchText(test.category);
        const subjectText = getEntitySearchText(test.subject);

        return (
          test.name?.toLowerCase().includes(query) ||
          test.description?.toLowerCase().includes(query) ||
          categoryText.includes(query) ||
          subjectText.includes(query)
        );
      });
      setFilteredTests(filtered);
    }
  }, [searchQuery, tests]);

  // The list comes after the dialog took the focus (its Close button): the search takes it then.
  useEffect(() => {
    if (!loading) searchRef.current?.focus();
  }, [loading]);

  // A refusal takes the focus: the pressed button lost it while it was disabled.
  useEffect(() => {
    if (error && !adding) errorRef.current?.focus();
  }, [error, adding]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Escape, a click outside, Close and Cancel.
  const close = () => {
    if (adding) return;
    if (addedRef.current > 0) onTestAdded();
    else onClose();
  };

  const handleTestSelect = (test) => {
    setSelectedTests(prev => {
      const isSelected = prev.some(selected => selected.uuid === test.uuid);
      if (isSelected) {
        // Remove test from selected
        return prev.filter(selected => selected.uuid !== test.uuid);
      } else {
        // Add test to selected
        return [...prev, test];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTests.length === filteredTests.length) {
      // Deselect all
      setSelectedTests([]);
    } else {
      // Select all filtered tests
      setSelectedTests(filteredTests);
    }
  };

  const handleAddTests = async () => {
    if (selectedTests.length === 0) return;

    try {
      setAdding(true);
      setError(null);

      console.log(`Adding ${selectedTests.length} tests to exam...`);
      
      // Send requests sequentially for each test
      const results = [];
      let currentOrder = existingTests.length + addedRef.current + 1;

      for (const test of selectedTests) {
        try {
          const examTestData = {
            exam: examUuid,
            test: test.uuid,
            order: currentOrder,
            weight: 1,
            is_required: true
          };

          console.log(`Adding test ${test.name} to exam:`, examTestData);
          
          await addTestToExam({
            params: { uuid: examUuid }, 
            data: examTestData 
          });
          
          results.push({ test, success: true });
          currentOrder++;
          addedRef.current += 1;
        } catch (err) {
          console.error(`Error adding test ${test.name}:`, err);
          results.push({ test, success: false, error: err });
          // Archived meanwhile: the exam takes no test now, so the rest are not tried.
          if (getApiErrorCode(err) === 'exam_archived') break;
        }
      }

      // Check results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      console.log(`Successfully added ${successful.length} tests to exam`);

      const archived = failed.find((f) => getApiErrorCode(f.error) === 'exam_archived');
      if (archived) {
        // The exam takes no test now: its page, read-only, says why (and shows those added before).
        onExamChanged?.(archived.error);
        return;
      }
      if (failed.length === 0) {
        // All tests added successfully
        onTestAdded();
        return;
      }

      // Those added are in the exam and no longer offered; the others stay selected, to try again.
      const added = new Set(successful.map((r) => r.test.uuid));
      if (added.size) {
        setTests((prev) => prev.filter((test) => !added.has(test.uuid)));
        setSelectedTests((prev) => prev.filter((test) => !added.has(test.uuid)));
      }
      const failedNames = failed.map(f => f.test.name).join(', ');
      const message = t('exam_bank.select_tests.add_failed', { names: failedNames });
      const raced = failed.some((f) => getApiErrorCode(f.error) === 'conflict');
      setError(raced ? `${message} ${t('errors.conflict')}` : message);

    } catch (err) {
      console.error('Error adding tests to exam:', err);
      setError(getCodedErrorMessage(t, err, t('exam_bank.select_tests.add_error')));
    } finally {
      setAdding(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <Dialog open onClose={close} initialFocus={closeRef} className="relative z-50">
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <Dialog.Panel className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5 motion-safe:animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-6 pb-4 pt-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600" aria-hidden="true">
              <BookOpenIcon className="h-5 w-5" />
            </div>
            <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">{t('exam_bank.select_tests.title')}</Dialog.Title>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            disabled={adding}
            aria-label={t('buttons.close')}
            className="-mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 sm:mr-0 sm:h-9 sm:w-9"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div role="status" className="flex items-center justify-center gap-3 px-6 pb-10 pt-4 text-sm text-slate-500">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true"></div>
            <span>{t('exam_bank.select_tests.loading')}</span>
          </div>
        ) : (
        <>
        {/* Search */}
        <div className="border-b border-slate-200 px-6 pb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label={t('exam_bank.select_tests.search_placeholder')}
              placeholder={t('exam_bank.select_tests.search_placeholder')}
              className="block h-10 w-full rounded-lg border border-slate-300 pl-10 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15"
            />
          </div>

          {filteredTests.length > 0 && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500">
                {t('exam_bank.select_tests.found', { count: filteredTests.length })}
              </span>
              {/* 44px on phones; from sm up a plain link-sized button. */}
              <button
                type="button"
                onClick={handleSelectAll}
                className="inline-flex min-h-[2.75rem] items-center rounded-md px-2 py-1 text-sm font-medium text-blue-600 transition duration-200 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:min-h-0"
              >
                {selectedTests.length === filteredTests.length ? t('exam_bank.select_tests.deselect_all') : t('exam_bank.select_tests.select_all')}
              </button>
            </div>
          )}
        </div>

        {/* Tests List */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60 p-4 sm:p-6">
          {error && (
            <div ref={errorRef} tabIndex={-1} role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 outline-none">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  type="button"
                  onClick={clearError}
                  aria-label={t('buttons.close')}
                  className="rounded-md p-1 text-red-400 transition duration-200 hover:bg-red-100 hover:text-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {filteredTests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400" aria-hidden="true">
                <BookOpenIcon className="h-6 w-6" />
              </div>
              <h3 className="text-sm font-medium text-slate-900">
                {searchQuery ? t('exam_bank.select_tests.no_results') : t('exam_bank.select_tests.none_available')}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {searchQuery
                  ? t('exam_bank.select_tests.no_results_hint')
                  : t('exam_bank.select_tests.none_available_hint')
                }
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTests.map((test) => {
                const isSelected = selectedTests.some(selected => selected.uuid === test.uuid);
                const categoryLabel = getEntityLabel(test.category, '');
                const subjectLabel = getEntityLabel(test.subject, '');
                const titleId = `${listId}-${test.uuid}`;

                // A checkbox for the keyboard and screen readers too: Space or Enter ticks it. The box comes first,
                // as in the other lists that tick; the test's state is a dot in its line, not a coloured pill.
                return (
                  <div
                    key={test.uuid}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-labelledby={titleId}
                    tabIndex={0}
                    onClick={() => handleTestSelect(test)}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        handleTestSelect(test);
                      }
                    }}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border bg-white p-3.5 transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                      isSelected
                        ? 'border-blue-600 ring-4 ring-blue-600/10'
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    <div
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition duration-200 ${
                        isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected && (
                        <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 id={titleId} className="break-words text-sm font-medium text-slate-900">
                        {test.name || t('exam_bank.untitled')}
                      </h3>

                      {test.description && (
                        <p className="mt-0.5 line-clamp-2 break-words text-sm text-slate-500">{test.description}</p>
                      )}

                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        {categoryLabel && (
                          <span className="inline-flex items-center gap-1">
                            <TagIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                            {categoryLabel}
                          </span>
                        )}
                        {subjectLabel && (
                          <span>{t('exam_bank.select_tests.subject', { subject: subjectLabel })}</span>
                        )}
                        {test.duration_minutes > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <ClockIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                            {t('exam_bank.select_tests.minutes', { minutes: test.duration_minutes })}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${test.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden="true" />
                          {test.is_active ? t('exam_bank.status.active') : t('exam_bank.status.inactive')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-sm text-slate-500">
            {selectedTests.length > 0 ? (
              <span>
                {t('exam_bank.select_tests.selected')} <strong className="font-semibold text-slate-900">{selectedTests.length}</strong>
                {selectedTests.length <= 3 && (
                  <span className="ml-2">
                    ({selectedTests.map(test => test.name).join(', ')})
                  </span>
                )}
              </span>
            ) : (
              <span>{t('exam_bank.select_tests.select_prompt')}</span>
            )}
          </div>

          {/* Phones: one row, the add button filling it (44px). */}
          <div className="flex shrink-0 items-center justify-end gap-3">
            <button
              type="button"
              onClick={close}
              disabled={adding}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10"
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAddTests}
              disabled={adding || selectedTests.length === 0}
              className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none max-sm:flex-1 sm:h-10"
            >
              {adding && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
              )}
              {adding
                ? t('exam_bank.select_tests.adding', { count: selectedTests.length })
                : selectedTests.length > 0
                  ? t('exam_bank.select_tests.add_count', { count: selectedTests.length })
                  : t('exam_bank.select_tests.add')
              }
            </button>
          </div>
        </div>
        </>
        )}
      </Dialog.Panel>
    </div>
    </Dialog>
  );
};

export default AddTestToExam;